import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { Offcanvas, Button, Badge, Alert, Table, Row, Col, Card, Spinner } from 'react-bootstrap';
import Swal from 'sweetalert2';
import './ActivityLogs.css';

const ActivityLogs = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        model_type: '',
        action: '',
        search: '',
        page: 1,
        per_page: 25
    });
    const [statistics, setStatistics] = useState({
        model_types: [],
        actions: [],
        recent_users: []
    });
    const [selectedLog, setSelectedLog] = useState(null);
    const [showOffcanvas, setShowOffcanvas] = useState(false);
    const [activeTab, setActiveTab] = useState('overview'); // This is for individual log details
    const [mainTab, setMainTab] = useState('activity'); // 'activity' or 'backend'
    const [backendEntries, setBackendEntries] = useState([]);
    const [backendLoading, setBackendLoading] = useState(false);
    const [pagination, setPagination] = useState({
        total: 0,
        per_page: 25,
        current_page: 1,
        last_page: 1
    });
    const lastFetchedParamsRef = useRef('');
    
    // Fetch logs
    const fetchLogs = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            let params = { page: page || filters.page };

            if (filters.model_type) params.model_type = filters.model_type;
            if (filters.action) params.action = filters.action;
            if (filters.user_id) params.user_id = filters.user_id;
            if (filters.start_date) params.start_date = filters.start_date;
            if (filters.end_date) params.end_date = filters.end_date;
            if (filters.search) params.search = filters.search;
            if (filters.per_page) params.per_page = filters.per_page;

            const paramsString = JSON.stringify(params);
            if (lastFetchedParamsRef.current === paramsString) return;
            lastFetchedParamsRef.current = paramsString;

            const response = await axios.get(`${backendUrl}/api/activity-logs`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                params,
            });

            if (response.data.success) {
                const { logs, statistics } = response.data;

                setLogs(logs.data);
                setStatistics(statistics);
                setPagination({
                    total: logs.total,
                    per_page: logs.per_page,
                    current_page: logs.current_page,
                    last_page: logs.last_page
                });

                setFilters(prev => ({ ...prev, page: logs.current_page }));
            }
        } catch (error) {
            console.error('Error fetching activity logs:', error);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        if (mainTab === 'activity') {
            fetchLogs(filters.page);
        } else if (mainTab === 'backend') {
            fetchBackendLogs();
        }
    }, [fetchLogs, filters.page, mainTab]);

    const fetchBackendLogs = async () => {
        setBackendLoading(true);
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/logs`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
            });

            setBackendEntries(response.data.entries || []);
        } catch (error) {
            console.error('Error fetching backend logs:', error);
        } finally {
            setBackendLoading(false);
        }
    };

    const clearBackendLogs = async () => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "This will clear all Laravel backend logs. You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, clear it!'
        });

        if (result.isConfirmed) {
            try {
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                const userInfo = JSON.parse(localStorage.getItem('user-info'));
                const origin = localStorage.getItem('user-origin');
                const token = userInfo?.token;

                await axios.delete(`${backendUrl}/api/logs`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    },
                });

                setBackendEntries([]);
                showSuccessToast('Logs cleared successfully!');
            } catch (error) {
                console.error('Error clearing logs:', error);
                showErrorToast('Failed to clear logs');
            }
        }
    };

    const showSuccessToast = (message) => {
        Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'success',
            title: message,
            timer: 3000,
            showConfirmButton: false,
        });
    };

    const showErrorToast = (message) => {
        Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'error',
            title: message,
            timer: 3000,
            showConfirmButton: false,
        });
    };

    const handlePageChange = (page) => {
        setFilters(prev => ({
            ...prev,
            page: page
        }));
    };

    const handleFilterChange = (name, value) => {
        setFilters(prev => ({
            ...prev,
            [name]: value,
            page: 1
        }));
    };

    const handleResetFilters = () => {
        setFilters({
            model_type: '',
            action: '',
            search: '',
            page: 1,
            per_page: 25
        });
    };

    const openLogDetails = (log) => {
        setSelectedLog(log);
        setShowOffcanvas(true);
    };

    const closeOffcanvas = () => {
        setShowOffcanvas(false);
        setTimeout(() => {
            setSelectedLog(null);
            setActiveTab('overview');
        }, 300);
    };

    const getActionBadgeClass = (action) => {
        switch (action) {
            case 'created': return 'badge-success';
            case 'updated': return 'badge-warning';
            case 'deleted': return 'badge-danger';
            default: return 'badge-secondary';
        }
    };

    const getActionBadgeColor = (action) => {
        switch(action) {
            case 'created': return 'success';
            case 'updated': return 'warning';
            case 'deleted': return 'danger';
            default: return 'secondary';
        }
    };

    const getLogLevelBadgeColor = (level) => {
        switch (level) {
            case 'ERROR':
            case 'CRITICAL':
            case 'ALERT':
            case 'EMERGENCY':
                return 'danger';
            case 'WARNING':
                return 'warning';
            case 'INFO':
            case 'NOTICE':
                return 'success';
            case 'DEBUG':
                return 'info';
            default:
                return 'secondary';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString || dateString === 'N/A') return '—';
        try {
            return dateString.includes('T') ? format(new Date(dateString), 'PPpp') : dateString;
        } catch {
            return dateString;
        }
    };

    const getModelIcon = (modelType) => {
        const icons = {
            'App\\Models\\Transaction': '📊',
            'App\\Models\\User': '👤',
            'App\\Models\\Client': '🏢',
            'App\\Models\\ProformaInvoice': '🧾',
            'App\\Models\\ProjectExpense': '💰',
            'App\\Models\\Category': '📂',
            'App\\Models\\BankAccount': '🏦',
        };
        return icons[modelType] || '📄';
    };

    const filterUnimportantFields = (data) => {
        if (!data || Object.keys(data).length === 0) return {};
        
        const unimportantFields = [
            'id', '_id', 'created_at', 'updated_at', 'deleted_at', 
            'created_by', 'updated_by', 'deleted_by',
            'createdAt', 'updatedAt', 'deletedAt',
            'tenant_id', 'company_id', 'branch_id',
            'remember_token', 'email_verified_at', 'password',
            'password_confirmation', 'token', 'api_token'
        ];
        
        const filteredData = {};
        Object.entries(data).forEach(([key, value]) => {
            if (!unimportantFields.includes(key) && !key.endsWith('_id') && value !== undefined && value !== null) {
                filteredData[key] = value;
            }
        });
        
        return filteredData;
    };

    const formatFieldName = (fieldName) => {
        return fieldName
            .replace(/_/g, ' ')
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();
    };

    const formatFieldValue = (fieldName, value) => {
        if (value === null || value === undefined || value === '') return '—';
        
        if (fieldName.includes('date') || fieldName.includes('Date') || 
            fieldName.includes('_at') || fieldName.endsWith('_date')) {
            try {
                return format(new Date(value), 'PPpp');
            } catch {
                return String(value);
            }
        }
        
        if (fieldName.includes('amount') || fieldName.includes('price') || 
            fieldName.includes('total') || fieldName.includes('balance')) {
            if (typeof value === 'number') {
                return new Intl.NumberFormat('en-IN', {
                    style: 'currency',
                    currency: 'INR',
                }).format(value);
            }
        }
        
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        if (typeof value === 'object') return JSON.stringify(value);
        
        const strValue = String(value);
        return strValue.length > 50 ? strValue.substring(0, 47) + '...' : strValue;
    };

    const getChangedFields = (oldData, newData) => {
        const changedFields = {};
        if (!oldData || !newData) return changedFields;
        
        const filteredOldData = filterUnimportantFields(oldData);
        const filteredNewData = filterUnimportantFields(newData);
        
        Object.keys(filteredNewData).forEach(key => {
            const oldValue = filteredOldData[key];
            const newValue = filteredNewData[key];
            
            if (oldValue !== newValue) {
                changedFields[key] = { old: oldValue, new: newValue };
            }
        });
        
        return changedFields;
    };

    const getImportantCreatedFields = (data) => {
        if (!data) return {};
        const filteredData = filterUnimportantFields(data);
        return Object.fromEntries(Object.entries(filteredData).slice(0, 10));
    };

    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="page-title-box d-flex align-items-center justify-content-between">
                                <h4 className="mb-0">Logs & Monitoring</h4>
                            </div>
                        </div>
                    </div>

                    <div className="row mb-4">
                        <div className="col-12">
                            <ul className="nav nav-pills nav-pills-custom bg-white p-2 rounded shadow-sm" style={{ width: 'fit-content' }}>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link border-0 px-4 fw-medium ${mainTab === 'activity' ? 'active shadow-sm' : 'text-muted'}`}
                                        onClick={() => setMainTab('activity')}
                                    >
                                        <i className="bx bx-list-ul me-1"></i> System Activity
                                    </button>
                                </li>
                                <li className="nav-item ms-2">
                                    <button
                                        className={`nav-link border-0 px-4 fw-medium ${mainTab === 'backend' ? 'active shadow-sm' : 'text-muted'}`}
                                        onClick={() => setMainTab('backend')}
                                    >
                                        <i className="bx bx-terminal me-1"></i> Backend Error Logs
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </div>

                    {mainTab === 'activity' ? (
                        <>
                            <div className="stats-grid mb-4">
                                <div className="stat-card">
                                    <div className="stat-icon text-primary bg-soft-primary">📈</div>
                                    <div className="stat-content">
                                        <div className="stat-label text-muted">Total Logs</div>
                                        <div className="stat-value">{pagination.total}</div>
                                    </div>
                                </div>
                                <div className="stat-card">
                                    <div className="stat-icon text-info bg-soft-info">📁</div>
                                    <div className="stat-content">
                                        <div className="stat-label text-muted">Model Types</div>
                                        <div className="stat-value">{statistics.model_types.length}</div>
                                    </div>
                                </div>
                                <div className="stat-card">
                                    <div className="stat-icon text-success bg-soft-success">👥</div>
                                    <div className="stat-content">
                                        <div className="stat-label text-muted">Active Users</div>
                                        <div className="stat-value">{statistics.recent_users.length}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="filters-card shadow-sm border-0 mb-4">
                                <div className="filters-header border-bottom p-3">
                                    <h5 className="mb-0 fs-16"><i className="bx bx-filter-alt me-1 text-primary"></i> Filter Results</h5>
                                </div>
                                <div className="card-body p-3">
                                    <div className="row g-3">
                                        <div className="col-md-4">
                                            <label className="form-label small fw-bold">Model Type</label>
                                            <select
                                                value={filters.model_type}
                                                onChange={(e) => handleFilterChange('model_type', e.target.value)}
                                                className="form-select form-select-sm"
                                            >
                                                <option value="">All Models</option>
                                                {statistics.model_types.map((model) => (
                                                    <option key={model.model_type} value={model.model_type}>
                                                        {model.model_type.split('\\').pop()}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label small fw-bold">Action</label>
                                            <select
                                                value={filters.action}
                                                onChange={(e) => handleFilterChange('action', e.target.value)}
                                                className="form-select form-select-sm"
                                            >
                                                <option value="">All Actions</option>
                                                <option value="created">Created</option>
                                                <option value="updated">Updated</option>
                                                <option value="deleted">Deleted</option>
                                            </select>
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label small fw-bold">Keyword Search</label>
                                            <div className="input-group input-group-sm">
                                                <span className="input-group-text bg-white border-end-0"><i className="bx bx-search"></i></span>
                                                <input
                                                    type="text"
                                                    className="form-control border-start-0"
                                                    placeholder="Search records..."
                                                    value={filters.search}
                                                    onChange={(e) => handleFilterChange('search', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <Card className="border-0 shadow-sm">
                                <Card.Body className="p-0">
                                    <div className="table-responsive">
                                        <Table hover className="align-middle mb-0">
                                            <thead className="table-sales-gradient text-muted small text-uppercase">
                                                <tr>
                                                    <th className="ps-4" style={{ width: '180px' }}>Date & Time</th>
                                                    <th style={{ width: '220px' }}>User</th>
                                                    <th style={{ width: '150px' }}>Action</th>
                                                    <th style={{ width: '150px' }}>Model</th>
                                                    <th style={{ width: '100px' }}>Record ID</th>
                                                    <th style={{ width: '140px' }}>IP Address</th>
                                                    <th className="text-center pe-4" style={{ width: '80px' }}>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading ? (
                                                    <tr>
                                                        <td colSpan="7" className="text-center py-5">
                                                            <Spinner animation="border" variant="primary" size="sm" className="me-2" />
                                                            <span className="text-muted">Loading activity data...</span>
                                                        </td>
                                                    </tr>
                                                ) : logs.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="7" className="text-center py-5">
                                                            <div className="text-muted">No activity logs match your filters</div>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    logs.map((log) => (
                                                        <tr key={log.id}>
                                                            <td className="ps-4 text-muted small" style={{ whiteSpace: 'nowrap' }}>{formatDate(log.created_at)}</td>
                                                            <td>
                                                                <div className="d-flex align-items-center">
                                                                    <div className="avatar-xs me-2 flex-shrink-0">
                                                                        <span className="avatar-title rounded-circle bg-soft-primary text-primary font-size-12">
                                                                            {log.user?.name?.charAt(0) || 'U'}
                                                                        </span>
                                                                    </div>
                                                                    <div className="text-truncate" style={{ maxWidth: '180px' }}>
                                                                        <div className="fw-medium small text-truncate">{log.user?.name || 'System'}</div>
                                                                        <div className="text-muted extra-small text-truncate">{log.user?.email}</div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <Badge bg={getActionBadgeColor(log.action)} className="text-capitalize px-2 py-1" style={{ minWidth: '100px' }}>
                                                                    {log.action.replace(/_/g, ' ')}
                                                                </Badge>
                                                                {log.action === 'deleted' && log.old_data?.deletion_reason && (
                                                                    <i 
                                                                        className="bx bx-comment-detail ms-1 text-primary cursor-pointer" 
                                                                        title={`Reason: ${log.old_data.deletion_reason}`}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            openLogDetails(log);
                                                                        }}
                                                                    ></i>
                                                                )}
                                                            </td>
                                                            <td className="small text-truncate" style={{ maxWidth: '150px' }}>
                                                                <span className="me-1">{getModelIcon(log.model_type)}</span>
                                                                {log.model_type.split('\\').pop()}
                                                            </td>
                                                            <td className="text-muted small">#{log.model_id}</td>
                                                            <td style={{ whiteSpace: 'nowrap' }}><code className="text-secondary small">{log.ip_address}</code></td>
                                                            <td className="text-center pe-4">
                                                                <Button 
                                                                    variant="light" 
                                                                    size="sm" 
                                                                    onClick={() => openLogDetails(log)}
                                                                    className="btn-icon"
                                                                >
                                                                    <i className="bx bx-show-alt text-primary fs-18"></i>
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Card.Body>
                            </Card>
                        </>
                    ) : (
                        <Card className="border-0 shadow-sm overflow-hidden">
                            <Card.Header className="bg-white py-3 px-4 d-flex justify-content-between align-items-center border-bottom">
                                <div>
                                    <h5 className="mb-0 fs-16"><i className="bx bx-terminal me-1 text-primary"></i> Backend Log Entries</h5>
                                    <p className="text-muted extra-small mb-0">Tabular view of Laravel logs with status indicators.</p>
                                </div>
                                <div className="d-flex gap-2">
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={fetchBackendLogs}
                                        disabled={backendLoading}
                                        className="d-flex align-items-center"
                                    >
                                        <i className={`bx bx-refresh ${backendLoading ? 'bx-spin' : ''} me-1`}></i> Refresh
                                    </Button>
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={clearBackendLogs}
                                        disabled={backendLoading || backendEntries.length === 0}
                                        className="d-flex align-items-center"
                                    >
                                        <i className="bx bx-trash me-1"></i> Clear Log File
                                    </Button>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-0">
                                <div className="table-responsive" style={{ maxHeight: '72vh', overflowY: 'auto' }}>
                                    <Table hover className="align-middle mb-0">
                                        <thead className="table-sales-gradient sticky-top shadow-sm small text-uppercase">
                                            <tr>
                                                <th className="ps-4" style={{ width: '180px' }}>Timestamp</th>
                                                <th style={{ width: '100px' }}>Level</th>
                                                <th style={{ width: '120px' }}>Status</th>
                                                <th>Message</th>
                                                <th className="pe-4 text-end" style={{ width: '80px' }}>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {backendLoading ? (
                                                <tr>
                                                    <td colSpan="5" className="text-center py-5">
                                                        <Spinner animation="border" variant="primary" size="sm" className="me-2" />
                                                        <span className="text-muted">Parsing server logs...</span>
                                                    </td>
                                                </tr>
                                            ) : backendEntries.length === 0 ? (
                                                <tr>
                                                    <td colSpan="5" className="text-center py-5 text-muted">
                                                        <i className="bx bx-check-circle fs-1 mb-3 text-success opacity-50"></i>
                                                        <h5>No log entries found</h5>
                                                        <p className="small">The Laravel log is currently clean.</p>
                                                    </td>
                                                </tr>
                                            ) : (
                                                backendEntries.map((entry, index) => (
                                                    <tr key={index} className={entry.is_error ? 'bg-soft-danger-row' : ''}>
                                                        <td className="ps-4 small text-muted font-monospace" style={{ whiteSpace: 'nowrap' }}>{formatDate(entry.timestamp)}</td>
                                                        <td>
                                                            <Badge bg={getLogLevelBadgeColor(entry.level)} className="px-2 py-1 font-size-10 text-uppercase" style={{ minWidth: '60px' }}>
                                                                {entry.level}
                                                            </Badge>
                                                        </td>
                                                        <td style={{ whiteSpace: 'nowrap' }}>
                                                            {entry.is_error ? (
                                                                <span className="text-danger small fw-bold">
                                                                    <i className="bx bx-error-circle me-1"></i> Error
                                                                </span>
                                                            ) : entry.is_success ? (
                                                                <span className="text-success small fw-bold">
                                                                    <i className="bx bx-check-circle me-1"></i> Success
                                                                </span>
                                                            ) : (
                                                                <span className="text-info small fw-bold">
                                                                    <i className="bx bx-info-circle me-1"></i> Trace
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="small">
                                                            <div 
                                                                style={{ 
                                                                    maxWidth: '100%', 
                                                                    wordBreak: 'break-word',
                                                                    whiteSpace: 'pre-wrap',
                                                                    maxHeight: '400px',
                                                                    overflowY: 'auto'
                                                                }}
                                                            >
                                                                {entry.message}
                                                            </div>
                                                        </td>
                                                        <td className="pe-4 text-end">
                                                            <Button 
                                                                variant="ghost-primary" 
                                                                size="sm" 
                                                                onClick={() => {
                                                                    Swal.fire({
                                                                        title: `Log Detail: ${entry.level}`,
                                                                        html: `
                                                                            <div style="text-align: left; font-family: 'Courier New', Courier, monospace; font-size: 13px; background: #2b2b2b; color: #f8f8f2; padding: 20px; border-radius: 8px; max-height: 600px; overflow-y: scroll; white-space: pre-wrap; border: 1px solid #444; box-shadow: inset 0 0 10px rgba(0,0,0,0.5);">
                                                                                ${entry.raw}
                                                                            </div>
                                                                        `,
                                                                        width: '90%',
                                                                        confirmButtonText: 'Close',
                                                                        confirmButtonColor: '#556ee6'
                                                                    })
                                                                }}
                                                            >
                                                                <i className="bx bx-detail fs-18"></i>
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </Card.Body>
                        </Card>
                    )}
                </div>
            </div>

            <Offcanvas
                show={showOffcanvas}
                onHide={closeOffcanvas}
                placement="end"
                className="activity-log-offcanvas"
                style={{ width: '600px' }}
            >
                <Offcanvas.Header closeButton className="border-bottom">
                    <Offcanvas.Title>
                        <h5 className="mb-0 d-flex align-items-center">
                            Log Entry Detail
                            {selectedLog && (
                                <Badge bg={getActionBadgeColor(selectedLog.action)} className="ms-2 font-size-12 px-2 py-1 text-capitalize">
                                    {selectedLog.action}
                                </Badge>
                            )}
                        </h5>
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="p-0">
                    {selectedLog && (
                        <div className="d-flex flex-column h-100">
                            <div className="p-3 bg-light border-bottom">
                                <ul className="nav nav-pills nav-justified" role="tablist">
                                    {['overview', 'changes', 'raw'].map((tab) => (
                                        <li className="nav-item" key={tab}>
                                            <button
                                                className={`nav-link border-0 text-capitalize py-2 ${activeTab === tab ? 'active shadow-sm' : 'text-muted'}`}
                                                onClick={() => setActiveTab(tab)}
                                            >
                                                {tab}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="p-4 overflow-auto flex-grow-1">
                                {activeTab === 'overview' && (
                                    <div className="tab-pane fade show active">
                                        <Card className="border-0 bg-light mb-4">
                                            <Card.Body>
                                                <h6 className="text-primary border-bottom pb-2 mb-3 fw-bold">Metadata</h6>
                                                <div className="row g-3">
                                                    <div className="col-6">
                                                        <label className="text-muted extra-small text-uppercase fw-bold d-block">Target Entity</label>
                                                        <span className="small">{selectedLog.model_type.split('\\').pop()}</span>
                                                    </div>
                                                    <div className="col-6">
                                                        <label className="text-muted extra-small text-uppercase fw-bold d-block">Entity ID</label>
                                                        <span className="small">#{selectedLog.model_id}</span>
                                                    </div>
                                                    <div className="col-6">
                                                        <label className="text-muted extra-small text-uppercase fw-bold d-block">Date Context</label>
                                                        <span className="small">{formatDate(selectedLog.created_at)}</span>
                                                    </div>
                                                    <div className="col-6">
                                                        <label className="text-muted extra-small text-uppercase fw-bold d-block">User Location</label>
                                                        <code className="text-muted small">{selectedLog.ip_address}</code>
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>

                                        <Card className="border-0 bg-light">
                                            <Card.Body>
                                                <h6 className="text-primary border-bottom pb-2 mb-3 fw-bold">Actor Account</h6>
                                                <div className="d-flex align-items-center">
                                                    <div className="avatar-md me-3">
                                                        <span className="avatar-title rounded-circle bg-white text-primary fs-20 shadow-sm">
                                                            {selectedLog.user?.name?.charAt(0) || 'U'}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-1">{selectedLog.user?.name || 'System / Guest'}</h6>
                                                        <p className="text-muted small mb-0">{selectedLog.user?.email || 'automated@system.internal'}</p>
                                                    </div>
                                                </div>
                                                
                                                {selectedLog.action === 'sent_test_email' && selectedLog.new_data?.log && (
                                                    <div className="mt-4">
                                                        <h6 className="text-success fw-bold small text-uppercase mb-2">
                                                            <i className="bx bx-check-shield me-1"></i> Connection Detail Log
                                                        </h6>
                                                        <pre className="bg-dark text-light p-3 rounded font-monospace extra-small" style={{ whiteSpace: 'pre-wrap', maxHeight: '300px', overflowY: 'auto' }}>
                                                            {selectedLog.new_data.log}
                                                        </pre>
                                                    </div>
                                                )}

                                                <div className="mt-3">
                                                    <label className="text-muted extra-small text-uppercase fw-bold d-block">Device Signature</label>
                                                    <div className="p-2 bg-white rounded extra-small text-muted border">
                                                        {selectedLog.user_agent}
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </div>
                                )}

                                {activeTab === 'changes' && (
                                    <div className="tab-pane fade show active">
                                        <div className="changes-timeline">
                                            {selectedLog.action === 'updated' ? (
                                                <div className="table-responsive border rounded bg-white">
                                                    <Table className="mb-0 small">
                                                        <thead className="table-sales-gradient">
                                                            <tr>
                                                                <th>Property</th>
                                                                <th>From</th>
                                                                <th>To</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {(() => {
                                                                const changes = getChangedFields(selectedLog.old_data, selectedLog.new_data);
                                                                if (Object.keys(changes).length === 0) {
                                                                    return <tr><td colSpan="3" className="text-center py-4 text-muted italic">No structural data changed</td></tr>;
                                                                }
                                                                return Object.entries(changes).map(([field, delta]) => (
                                                                    <tr key={field}>
                                                                        <td className="fw-bold">{formatFieldName(field)}</td>
                                                                        <td className="text-danger bg-soft-danger"><del>{formatFieldValue(field, delta.old)}</del></td>
                                                                        <td className="text-success bg-soft-success fw-medium">{formatFieldValue(field, delta.new)}</td>
                                                                    </tr>
                                                                ));
                                                            })()}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            ) : (
                                                <div className="bg-white border rounded p-4">
                                                    <div className="text-center mb-4">
                                                        <i className={`bx ${selectedLog.action === 'created' ? 'bx-plus-circle text-success' : 'bx-trash text-danger'} fs-1 mb-2`}></i>
                                                        <h5>Record {selectedLog.action === 'created' ? 'Creation' : 'Deletion'}</h5>
                                                    </div>

                                                    {selectedLog.action === 'deleted' && selectedLog.old_data?.deletion_reason && (
                                                        <div className="mb-4 p-3 bg-soft-danger border border-danger border-opacity-25 rounded shadow-inner">
                                                            <div className="d-flex align-items-center mb-2">
                                                                <i className="bx bx-info-circle text-danger me-2 font-size-18"></i>
                                                                <h6 className="fw-bold text-danger mb-0">Deletion Reason</h6>
                                                            </div>
                                                            <p className="mb-0 text-dark small" style={{ whiteSpace: 'pre-wrap' }}>
                                                                {selectedLog.old_data.deletion_reason}
                                                            </p>
                                                        </div>
                                                    )}

                                                    <p className="text-muted small mt-3 text-center">Snapshot is available in the Raw Data tab.</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'raw' && (
                                    <div className="tab-pane fade show active">
                                        <div className="p-3 bg-dark rounded border border-secondary shadow-inner">
                                            <pre className="text-info m-0 extra-small" style={{ maxHeight: '60vh' }}>
                                                {JSON.stringify(selectedLog, null, 2)}
                                            </pre>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </Offcanvas.Body>
            </Offcanvas>
            
            <style dangerouslySetInnerHTML={{ __html: `
                .bg-soft-primary { background-color: rgba(85, 110, 230, 0.1); }
                .bg-soft-info { background-color: rgba(80, 165, 241, 0.1); }
                .bg-soft-success { background-color: rgba(52, 195, 143, 0.1); }
                .bg-soft-danger { background-color: rgba(244, 106, 106, 0.1); }
                .bg-soft-danger-row { background-color: rgba(244, 106, 106, 0.03); }
                .extra-small { font-size: 11px; }
                .font-size-20 { font-size: 20px; }
                .font-size-10 { font-size: 10px; }
                .shadow-inner { box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.06); }
                .nav-pills-custom .nav-link { border-radius: 6px; transition: all 0.2s ease; }
                .nav-pills-custom .nav-link.active { background-color: #556ee6; color: white; }
                .avatar-md { height: 48px; width: 48px; }
                .avatar-xs { height: 24px; width: 24px; }
                .avatar-title { height: 100%; width: 100%; display: flex; align-items: center; justify-content: center; }
                .btn-icon { width: 32px; height: 32px; padding: 0; display: inline-flex; align-items: center; justify-content: center; border-radius: 6px; }
                .btn-ghost-primary { background: transparent; border: none; color: #556ee6; }
                .btn-ghost-primary:hover { background: rgba(85, 110, 230, 0.1); }
                .text-truncate-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
                .clickable-message { cursor: pointer; }
                .sticky-top { z-index: 10; }
            `}} />
        </div>
    );
};

export default ActivityLogs;
